import { LTV_THRESHOLDS, LTV_COLORS, VALIDATION_RULES } from './constants';

// Currency formatting
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Percentage formatting
export const formatPercentage = (value, decimals = 1) => {
  return `${(value * 100).toFixed(decimals)}%`;
};

// Date formatting
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Date and time formatting
export const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calculate LTV (Loan-to-Value)
export const calculateLTV = (requestedAmount, valuation) => {
  if (!valuation || valuation === 0) return 0;
  return requestedAmount / valuation;
};

// Get LTV color based on threshold
export const getLTVColor = (ltv) => {
  if (ltv <= LTV_THRESHOLDS.good) return LTV_COLORS.good;
  if (ltv <= LTV_THRESHOLDS.caution) return LTV_COLORS.caution;
  return LTV_COLORS.danger;
};

// Get LTV status text
export const getLTVStatus = (ltv) => {
  if (ltv <= LTV_THRESHOLDS.good) return 'Good';
  if (ltv <= LTV_THRESHOLDS.caution) return 'Caution';
  return 'High Risk';
};

// Calculate days until expiry
export const getDaysUntilExpiry = (expiryDate) => {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Check if listing is expired
export const isListingExpired = (expiryDate) => {
  return getDaysUntilExpiry(expiryDate) <= 0;
};

// Calculate commitment percentage
export const calculateCommitmentPercentage = (committedAmount, requestedAmount) => {
  if (!requestedAmount || requestedAmount === 0) return 0;
  return Math.min((committedAmount / requestedAmount) * 100, 100);
};

// Check if listing is fully funded
export const isListingFullyFunded = (committedAmount, requestedAmount) => {
  return committedAmount >= requestedAmount;
};

// Validate LTV for lien position
export const validateLTVForLien = (ltv, lienPosition) => {
  if (lienPosition === 'FirstLien') {
    return ltv <= VALIDATION_RULES.maxLtvForFirstLien;
  }
  if (lienPosition === 'SecondLien') {
    return ltv <= VALIDATION_RULES.maxLtvForSecondLien;
  }
  return true;
};

// Check if property is eligible for listing
export const isPropertyEligibleForListing = (property, desiredLien) => {
  // Check if property is eligible
  if (!property.eligible) return false;
  
  // Check if property is in underwriting
  if (property.inUnderwriting) return false;
  
  // Check lien position compatibility
  if (desiredLien === 'FirstLien' && property.lienPosition !== 'Unencumbered') {
    return false;
  }
  
  if (desiredLien === 'SecondLien' && property.lienPosition === 'SecondLien') {
    return false;
  }
  
  return true;
};

// Generate listing title
export const generateListingTitle = (property, requestedAmount, termMonths) => {
  const location = property.address.split(',').slice(-2).join(',').trim();
  const amount = formatCurrency(requestedAmount);
  const term = termMonths === 12 ? '1 year' : `${termMonths} months`;
  
  return `CRE: ${property.address.split(',')[0]} (${location}) — ${amount} ${term}`;
};

// Filter listings by criteria
export const filterListings = (listings, filters) => {
  return listings.filter(listing => {
    // Asset type filter
    if (filters.assetType && filters.assetType !== 'all' && listing.badges[0] !== filters.assetType) {
      return false;
    }
    
    // LTV range filter
    if (filters.ltvRange && (listing.ltv < filters.ltvRange[0] || listing.ltv > filters.ltvRange[1])) {
      return false;
    }
    
    // Location filter
    if (filters.location && filters.location !== 'all' && !listing.location.includes(filters.location)) {
      return false;
    }
    
    // Lien position filter
    if (filters.lienPosition && filters.lienPosition !== 'all') {
      const hasLienBadge = listing.badges.some(badge => 
        badge.includes(filters.lienPosition === 'FirstLien' ? '1st-Lien' : '2nd-Lien')
      );
      if (!hasLienBadge) return false;
    }
    
    // Status filter
    if (filters.status && filters.status !== 'all' && listing.status !== filters.status) {
      return false;
    }
    
    return true;
  });
};

// Sort listings
export const sortListings = (listings, sortBy, sortOrder = 'asc') => {
  return [...listings].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'ltv':
        aValue = a.ltv;
        bValue = b.ltv;
        break;
      case 'amount':
        aValue = a.requestedAmount;
        bValue = b.requestedAmount;
        break;
      case 'created':
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case 'expires':
        aValue = new Date(a.expiresAt);
        bValue = new Date(b.expiresAt);
        break;
      default:
        aValue = a.title;
        bValue = b.title;
    }
    
    if (sortOrder === 'desc') {
      return bValue > aValue ? 1 : -1;
    }
    return aValue > bValue ? 1 : -1;
  });
};

// Check investor mandate compatibility
export const isInvestorCompatible = (investor, listing, property) => {
  const mandates = investor.mandates;
  
  // Check asset type
  if (!mandates.assetTypes.includes('Property')) {
    return false;
  }
  
  // Check LTV
  if (listing.ltv > mandates.maxLtv) {
    return false;
  }
  
  // Check ticket size
  if (listing.requestedAmount < mandates.minTicket || listing.requestedAmount > mandates.maxTicket) {
    return false;
  }
  
  // Check geography
  const listingState = listing.location.split(',')[1]?.trim();
  if (!mandates.geos.includes(listingState)) {
    return false;
  }
  
  // Check lien preference
  if (mandates.lienPreference !== 'Any' && !listing.badges.includes(mandates.lienPreference)) {
    return false;
  }
  
  // Check term range
  if (listing.desiredTermMonths < mandates.termRange[0] || listing.desiredTermMonths > mandates.termRange[1]) {
    return false;
  }
  
  // Check cap rate
  if (property.capRate < mandates.minCapRate) {
    return false;
  }
  
  return true;
};

// Generate commitment ID
export const generateCommitmentId = () => {
  return `cmt-${Date.now()}`;
};

// Generate listing ID
export const generateListingId = () => {
  return `lst-${Date.now()}`;
};

// Generate property ID
export const generatePropertyId = () => {
  return `prop-${Date.now()}`;
};

// Calculate monthly payment
export const calculateMonthlyPayment = (principal, annualRate, termMonths) => {
  const monthlyRate = annualRate / 100 / 12;
  const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                  (Math.pow(1 + monthlyRate, termMonths) - 1);
  return payment;
};

// Calculate total interest
export const calculateTotalInterest = (principal, annualRate, termMonths) => {
  const monthlyPayment = calculateMonthlyPayment(principal, annualRate, termMonths);
  return (monthlyPayment * termMonths) - principal;
};
